import type { QuietHours } from '@nw/shared';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, errorMessage } from '../../lib/api';
import { toast } from '../../components/ui/toast';

/** Types and TanStack Query hooks for the B5 (alerts / templates) admin API. See docs/modules/alerts.md. */

export type Lang = 'he' | 'en';
export type Scope = 'global' | 'client' | 'site';
export type TemplateSource = 'site' | 'client' | 'global' | 'builtin';
/** wave 2 (D-120): five scopes + the code default */
export type SettingSource = 'site' | 'client' | 'parent' | 'segment' | 'global' | 'default';

export interface Issue {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface TemplateRow {
  id: string;
  key: string;
  language: Lang;
  scope: Scope;
  scopeId: string | null;
  body: string;
  version: number;
  updatedBy: string | null;
  updatedAt: string;
}

export interface TemplateListItem {
  key: string;
  language: Lang;
  effective: {
    id: string | null;
    key: string;
    language: Lang;
    body: string;
    source: TemplateSource;
    version: number;
    updatedAt: string | null;
    updatedBy: string | null;
  };
  override: TemplateRow | null;
  inherited: { source: TemplateSource; body: string; version: number };
  localOverrides: number;
}

export interface TemplateList {
  scope: Scope;
  scopeId: string | null;
  items: TemplateListItem[];
}

export interface TemplateVersions {
  template: TemplateRow;
  versions: { version: number; body: string; createdBy: string | null; createdAt: string }[];
}

export interface PreviewResult {
  text: string;
  errors: Issue[];
  warnings: Issue[];
  dataSource: 'call' | 'sample';
  callUuid: string | null;
  bodySource: 'request' | TemplateSource;
}

export interface TemplateVariable {
  name: string;
  description: string;
  example?: string;
}

export interface Variables {
  key: string;
  variables: TemplateVariable[];
  fields: TemplateVariable[];
}

export type Severity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export interface SettingsValues {
  enabled: boolean;
  rules: { mode: 'default' | 'explicit'; ruleUuids: string[] };
  minSeverity: Severity | null;
  burstThreshold: number;
  burstWindowMin: number;
  staleHours: number;
  quietHours: QuietHours;
  editOnAck: boolean;
  /** D-134: minutes until the reminder of an unmarked critical alert (0 = off); absent on an older server */
  remindAfterMin?: number;
  extraFields: string[];
  /** D-90: NIVISION signals or the in-house car rules */
  ruleSource: RuleSource;
  /** D-99: per watch-rule config overrides (merged per rule and key) */
  watchConfig?: WatchConfig;
}
/** D-99: { [watchRuleId]: { [key]: value } } */
export type WatchRuleConfig = Record<string, number | string | string[]>;
export type WatchConfig = Record<string, WatchRuleConfig>;

/** D-99: POST /api/alerts/watch-preview */
export interface WatchPreviewResult {
  count7d: number;
  bySite: Record<string, number>;
}

/** GET /api/alerts/settings (one scope's own row; null fields = inherit) */
export interface SettingsRow {
  scope: Scope;
  scopeId: string | null;
  watchConfig?: WatchConfig | null;
}
export type SettingsField = keyof SettingsValues;
/** D-90 / D-103 / D-141: the in-house rulesets (mirrors the API's IN_HOUSE_SOURCES) */
export const IN_HOUSE_SOURCES = ['car', 'general', 'labs', 'retail', 'insurance'] as const;
export type InHouseSource = (typeof IN_HOUSE_SOURCES)[number];
/** NIVISION signals, or an in-house ruleset */
export type RuleSource = 'nivision' | InHouseSource;

export interface EffectiveSettings {
  clientId: string | null;
  siteId: string | null;
  values: SettingsValues;
  sources: Record<SettingsField, SettingSource>;
  defaults: Record<string, unknown>;
  /** D-134: what the client gets without its own value (the "ברירת מחדל" of the reminder select) */
  inherited?: { remindAfterMin: number };
}

export interface SettingsPut {
  scope: Scope;
  scopeId?: string | null;
  enabled?: boolean | null;
  enabledRuleUuids?: string[];
  rulesOverride?: boolean;
  minSeverity?: Severity | null;
  burstThreshold?: number | null;
  burstWindowMin?: number | null;
  staleHours?: number | null;
  quietHours?: QuietHours | null;
  editOnAck?: boolean | null;
  /** D-134: 0 = off; null = inherit */
  remindAfterMin?: 0 | 60 | 120 | 180 | null;
  extraFields?: string[];
  ruleSource?: RuleSource | null;
  watchConfig?: WatchConfig | null;
}

export interface Rule {
  ruleUuid: string;
  name: string;
  description: string | null;
  severity: Severity;
  isActive: boolean;
  fieldLabel: string | null;
  siteIds: string[];
  enabled: boolean;
  fires7d: number;
  syncedAt: string | null;
  /** D-90; D-99: 'watch' = a watch rule (not about one call), listed for every source */
  source?: RuleSource | 'watch';
  /** "מה עושים" of a car rule */
  action?: string | null;
  /** car rules: the rule's own default */
  defaultEnabled?: boolean;
  /** D-99 watch rules: the effective thresholds (defaults + watchConfig) and the rule's own defaults */
  config?: WatchRuleConfig;
  configDefaults?: WatchRuleConfig;
  /** NIVISION source (S8-11): false = the rule arrived on alerts but is not in the synced rules list yet */
  synced?: boolean;
}

export interface RulesResponse {
  clientId: string;
  siteId: string | null;
  ruleSource?: RuleSource;
  selection: { mode: 'default' | 'explicit'; ruleUuids: string[]; source: SettingSource; minSeverity: Severity | null; defaultMinSeverity: Severity };
  rules: Rule[];
}

export type AlertStatus =
  | 'pending'
  | 'queued'
  | 'batched'
  | 'sent'
  | 'skipped_stale'
  | 'skipped_rule'
  | 'skipped_inactive'
  | 'skipped_no_route'
  | 'failed';

export interface AlertItem {
  id: string;
  callUuid: string;
  ruleUuid: string;
  ruleName: string | null;
  severity: Severity;
  clientId: string | null;
  siteId: string | null;
  siteName: string | null;
  callTitle: string | null;
  callStartedAt: string | null;
  agentName: string | null;
  reportUrl: string | null;
  status: AlertStatus;
  reason: string | null;
  /** specific Hebrew explanation (which rule / client / what to change) */
  reasonDetail?: string | null;
  batchKey: string | null;
  groupId: string | null;
  outboxMessageId: string | null;
  /** a failed alert whose message the Outbox still retries: no "שלח שוב" yet (older servers: absent) */
  sendRetrying?: boolean;
  ack: { name: string | null; waId: string | null; at: string } | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface AlertPage {
  items: AlertItem[];
  nextCursor: string | null;
  total?: number;
}

export interface ExampleItem {
  key: 'alert' | 'alert_acked' | 'alert_batch' | 'daily' | 'weekly' | 'daily_fallback';
  title: string;
  text: string;
  source: 'global' | 'builtin';
}

/** D-90: POST /api/alerts/rules-test */
export interface RulesTestInput {
  clientId: string;
  /** the in-house ruleset to replay (default car) */
  ruleset?: InHouseSource;
  from: string;
  to: string;
  send?: { groupId: string; limit?: number };
}
export interface RulesTestResult {
  calls: number;
  withReport: number;
  byRule: { ruleUuid: string; name: string; severity: Severity; count: number }[];
  matches: {
    callUuid: string;
    siteName: string;
    agentName: string;
    startedAt: string;
    reportUrl: string;
    signals: { ruleUuid: string; ruleName: string; severity: Severity; fieldName: string; fieldValue: string }[];
  }[];
  sent: number;
}

/** the WhatsApp groups flagged as test groups (lane B2a list; empty without groups.view) */
export interface TestGroup {
  id: string;
  name: string;
  isTestGroup: boolean;
}

// ---------------------------------------------------------------- keys

export const b5Keys = {
  examples: (lang: Lang) => ['b5', 'examples', lang] as const,
  templates: (scope: Scope, scopeId?: string | null, key?: string) => ['b5', 'templates', scope, scopeId ?? null, key ?? null] as const,
  templatesAll: ['b5', 'templates'] as const,
  versions: (id: string) => ['b5', 'template-versions', id] as const,
  variables: (key: string, clientId?: string) => ['b5', 'variables', key, clientId ?? null] as const,
  preview: (p: PreviewInput) => ['b5', 'preview', p] as const,
  effective: (clientId: string) => ['b5', 'settings', 'effective', clientId] as const,
  settingsAll: ['b5', 'settings'] as const,
  rules: (clientId: string) => ['b5', 'rules', clientId] as const,
  settingsRow: (clientId: string) => ['b5', 'settings', 'row', clientId] as const,
  watchPreview: (clientId: string, ruleId: string, config: WatchRuleConfig) => ['b5', 'watch-preview', clientId, ruleId, config] as const,
  alerts: (clientId: string, status: string) => ['b5', 'alerts', clientId, status] as const,
  alertsAll: ['b5', 'alerts'] as const,
  overview: ['b5', 'settings', 'overview'] as const,
  // wave 2 (W2-2-1): scope pages, the matrix and the scope replay
  scope: (scope: string, id: string) => ['b5', 'scope', scope, id] as const,
  scopeAll: ['b5', 'scope'] as const,
  matrix: (f: { segment?: string; parentId?: string; status?: string }) => ['b5', 'matrix', f] as const,
  matrixAll: ['b5', 'matrix'] as const,
  scopeReplay: (scope: string, scopeId: string, ruleId: string, config: WatchRuleConfig) => ['b5', 'scope-replay', scope, scopeId, ruleId, config] as const,
};

// ---------------------------------------------------------------- queries

export function useExamples(lang: Lang) {
  return useQuery({ queryKey: b5Keys.examples(lang), queryFn: () => api<{ lang: Lang; items: ExampleItem[] }>('/api/examples', { query: { lang } }) });
}

export function useTemplates(scope: Scope, scopeId?: string | null, key?: string) {
  return useQuery({
    queryKey: b5Keys.templates(scope, scopeId, key),
    queryFn: () => api<TemplateList>('/api/templates', { query: { scope, scopeId: scopeId ?? undefined, key } }),
  });
}

export function useTemplateVersions(id: string | null | undefined) {
  return useQuery({
    queryKey: b5Keys.versions(id ?? ''),
    queryFn: () => api<TemplateVersions>(`/api/templates/${encodeURIComponent(id!)}/versions`),
    enabled: Boolean(id),
  });
}

export function useVariables(key: string, clientId?: string) {
  return useQuery({
    queryKey: b5Keys.variables(key, clientId),
    queryFn: () => api<Variables>('/api/templates/variables', { query: { key, clientId } }),
    staleTime: 5 * 60_000,
  });
}

export interface PreviewInput {
  key: string;
  language: Lang;
  body?: string;
  clientId?: string;
  siteId?: string;
}

/** Live preview. `input.body` should already be debounced by the caller. */
export function usePreview(input: PreviewInput, enabled = true) {
  return useQuery({
    queryKey: b5Keys.preview(input),
    queryFn: () => api<PreviewResult>('/api/templates/preview', { method: 'POST', body: { ...input } }),
    placeholderData: keepPreviousData,
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useEffectiveSettings(clientId: string) {
  return useQuery({
    queryKey: b5Keys.effective(clientId),
    queryFn: () => api<EffectiveSettings>('/api/alerts/settings/effective', { query: { clientId } }),
  });
}

export function useRules(clientId: string) {
  return useQuery({ queryKey: b5Keys.rules(clientId), queryFn: () => api<RulesResponse>('/api/alerts/rules', { query: { clientId } }) });
}

/** The client's own AlertSettings row (watchConfig is replaced as a whole on PUT, so a save merges into it). */
export function useClientSettingsRow(clientId: string, enabled = true) {
  return useQuery({
    queryKey: b5Keys.settingsRow(clientId),
    queryFn: () => api<SettingsRow>('/api/alerts/settings', { query: { scope: 'client', scopeId: clientId } }),
    enabled,
  });
}

/** D-99: how many alerts a watch rule would have sent in the last 7 days with these thresholds (the caller debounces them). */
export function useWatchPreview(clientId: string, ruleId: string, config: WatchRuleConfig, enabled = true) {
  return useQuery({
    queryKey: b5Keys.watchPreview(clientId, ruleId, config),
    queryFn: () => api<WatchPreviewResult>('/api/alerts/watch-preview', { method: 'POST', body: { clientId, ruleId, config } }),
    placeholderData: keepPreviousData,
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/** The agents of the client's sites (lane B3 list), for the "agents not checked" picker. */
export interface AgentOption {
  agentUuid: string;
  name: string;
  siteId: string;
}
export function useSiteAgents(siteIds: string[], enabled = true) {
  return useQuery({
    queryKey: ['nivision', 'agents-of', ...siteIds],
    enabled: enabled && siteIds.length > 0,
    queryFn: async (): Promise<AgentOption[]> => {
      const lists = await Promise.all(
        siteIds.map((siteId) =>
          api<{ items: { agentUuid: string; displayName: string | null; name: string | null }[] }>(`/api/nivision/sites/${encodeURIComponent(siteId)}/agents`).then((r) =>
            r.items.map((a) => ({ agentUuid: a.agentUuid, name: a.displayName ?? a.name ?? a.agentUuid, siteId })),
          ),
        ),
      );
      const seen = new Set<string>();
      return lists.flat().filter((a) => (seen.has(a.agentUuid) ? false : (seen.add(a.agentUuid), true))).sort((a, b) => a.name.localeCompare(b.name, 'he'));
    },
    staleTime: 60_000,
    retry: false,
  });
}

/** status filter: '' (all) | 'sent' | 'skipped' | 'failed' | 'open' (sent and not acked) */
export function useAlerts(clientId: string, status: string) {
  return useInfiniteQuery({
    queryKey: b5Keys.alerts(clientId, status),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api<AlertPage>('/api/alerts', {
        query: {
          clientId,
          limit: 20,
          cursor: pageParam ?? undefined,
          status: status === 'open' ? 'sent,queued,batched' : status === 'off' ? 'skipped_rule' : status || undefined,
          acked: status === 'open' ? 'false' : undefined,
        },
      }),
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** Light client info (lane B4). Optional: the tab works without it. */
export interface ClientLite {
  status?: string;
  id: string;
  name?: string;
  language?: Lang;
}
export function useClientLite(clientId: string) {
  return useQuery({
    queryKey: ['clients', clientId, 'lite-b5'],
    queryFn: async () => {
      const r = await api<Record<string, unknown>>(`/api/clients/${encodeURIComponent(clientId)}`);
      const c = (r.client && typeof r.client === 'object' ? r.client : r) as Record<string, unknown>;
      const lang = c.language === 'en' ? 'en' : c.language === 'he' ? 'he' : undefined;
      return { id: clientId, name: typeof c.name === 'string' ? c.name : undefined, language: lang, status: typeof c.status === 'string' ? c.status : undefined } satisfies ClientLite;
    },
    retry: false,
    staleTime: 60_000,
  });
}

/** The test group from Settings (safety.testGroupId) first, then groups flagged as test groups. Each source is optional. */
export function useTestGroups() {
  return useQuery({
    queryKey: ['groups', 'test', 'b5'],
    queryFn: async (): Promise<(TestGroup & { fromSettings?: boolean })[]> => {
      const [settings, flagged] = await Promise.all([
        api<{ groupId: string | null; group: { id: string; name: string } | null }>('/api/safety/test-group').catch(() => null),
        api<{ items: TestGroup[] }>('/api/groups', { query: { filter: 'test' } }).then((r) => r.items.filter((g) => g.isTestGroup)).catch(() => [] as TestGroup[]),
      ]);
      const sys = settings?.group ? [{ id: settings.group.id, name: settings.group.name, isTestGroup: true, fromSettings: true }] : [];
      return [...sys, ...flagged.filter((g) => g.id !== settings?.groupId)];
    },
    retry: false,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------- mutations

export function useRulesTest() {
  return useMutation({
    mutationFn: (body: RulesTestInput) => api<RulesTestResult>('/api/alerts/rules-test', { method: 'POST', body: { ...body } }),
    onError: (e) => toast(errorMessage(e), 'error'),
  });
}

export function usePutSettings(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<SettingsPut, 'scope' | 'scopeId'>) =>
      api<{ effective: { values: SettingsValues; sources: EffectiveSettings['sources'] } }>('/api/alerts/settings', {
        method: 'PUT',
        body: { scope: 'client', scopeId: clientId, ...body },
      }),
    onSuccess: (r) => {
      qc.setQueryData<EffectiveSettings>(b5Keys.effective(clientId), (old) => (old ? { ...old, values: r.effective.values, sources: r.effective.sources } : old));
      void qc.invalidateQueries({ queryKey: b5Keys.settingsAll });
      void qc.invalidateQueries({ queryKey: b5Keys.rules(clientId) });
      void qc.invalidateQueries({ queryKey: b5Keys.scopeAll });
      void qc.invalidateQueries({ queryKey: b5Keys.matrixAll });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
}

/** S5-12: turn one rule of the client's current source on (the routing screens' quick fix). */
export function useEnableRule(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleUuid: string) => api<{ enabled: true }>('/api/alerts/rules/enable', { method: 'POST', body: { clientId, ruleUuid } }),
    onSuccess: () => {
      toast('ההתראה הודלקה');
      void qc.invalidateQueries({ queryKey: b5Keys.rules(clientId) });
      void qc.invalidateQueries({ queryKey: b5Keys.settingsAll });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
}

export interface PutTemplateBody {
  key: string;
  language: Lang;
  scope: Scope;
  scopeId?: string | null;
  body: string;
}

export function usePutTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PutTemplateBody) =>
      api<{ template: TemplateRow; warnings: Issue[]; changed: boolean }>('/api/templates', { method: 'PUT', body: { ...body } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: b5Keys.templatesAll });
      void qc.invalidateQueries({ queryKey: b5Keys.versions(r.template.id) });
      void qc.invalidateQueries({ queryKey: ['b5', 'examples'] });
      void qc.invalidateQueries({ queryKey: ['b5', 'preview'] });
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ deleted: true }>(`/api/templates/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: b5Keys.templatesAll });
      void qc.invalidateQueries({ queryKey: ['b5', 'examples'] });
      void qc.invalidateQueries({ queryKey: ['b5', 'preview'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
}

export function useRestoreTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id: string; version: number }) =>
      api<{ template: TemplateRow; warnings: Issue[] }>(`/api/templates/${encodeURIComponent(p.id)}/restore`, { method: 'POST', body: { version: p.version } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: b5Keys.templatesAll });
      void qc.invalidateQueries({ queryKey: b5Keys.versions(r.template.id) });
      void qc.invalidateQueries({ queryKey: ['b5', 'examples'] });
      void qc.invalidateQueries({ queryKey: ['b5', 'preview'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
}

/** 422 TEMPLATE_INVALID -> the server's {errors, warnings}. */
export function templateIssues(e: unknown): { errors: Issue[]; warnings: Issue[] } | null {
  if (e instanceof ApiError && e.code === 'TEMPLATE_INVALID') {
    const d = (e.details ?? {}) as { errors?: Issue[]; warnings?: Issue[] };
    return { errors: d.errors ?? [], warnings: d.warnings ?? [] };
  }
  return null;
}

/** "Send anyway" for an alert skipped because the call was old — the server runs it through the normal flow. */
export function useSendAnyway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ id: string; status: AlertStatus; reason: string | null; reasonDetail: string | null }>(`/api/alerts/${encodeURIComponent(id)}/send-anyway`, { method: 'POST' }),
    // also after a 409 (ALERT_STILL_NOT_SENDABLE): the re-run re-decided the alert
    onSettled: () => void qc.invalidateQueries({ queryKey: b5Keys.alertsAll }),
    onError: (e) => toast(errorMessage(e), 'error'),
  });
}

// ---------------------------------------------------------------- task 27: clients-list overview (the bulk edit is the matrix's, W2-2-9)

export interface AlertOverviewState {
  ruleSource: RuleSource;
  enabled: boolean;
  rulesMode: 'default' | 'explicit';
  enabledRuleIds: string[];
  watchEnabled: string[];
  /** the watch rules' own on/off switches that are set (watchConfig[id].enabled) */
  watchFlags: Record<string, 0 | 1>;
  /** W2-1-12: rules with an own (client-scope) on / off, severity or param */
  overrides: number;
}
export interface AlertOverviewItem extends AlertOverviewState {
  clientId: string;
  /** W2-1-12: Client.segment (null = none) — the segment pill of the clients list (W2-2-9) */
  segment: string | null;
  morningEnabled: boolean;
}
export interface RuleCatalogItem {
  id: string;
  name: string;
  severity: string;
  defaultEnabled: boolean;
}
export interface AlertOverview {
  items: AlertOverviewItem[];
  /** D-141: `labs` may be missing from an older server */
  catalog: Record<InHouseSource, RuleCatalogItem[] | undefined> & { watch: RuleCatalogItem[] };
}
/** Task 27: the alert settings of every regular client (one batched call) + the in-house rule catalog. */
export function useAlertsOverview(enabled = true) {
  return useQuery({ queryKey: b5Keys.overview, queryFn: () => api<AlertOverview>('/api/alerts/settings/overview'), enabled, staleTime: 30_000 });
}

